/**
 * financingScenarios.ts
 * FAM — Diminishing Musharaka (Musharaka Mutanaqisah) Underwriting Engine
 *
 * Structure:
 *   - FAM + investors + gifters co-purchase the vehicle (Shirkat al-Milk)
 *   - Creator leases the unowned portion (Ijarah)
 *   - Monthly payment = Rent (on unowned share) + Unit Purchase (buying ownership)
 *   - Rent decreases as creator's ownership grows
 *   - Ends when creator owns 100% → title transfers
 *
 * Rent Rate Basis:
 *   Annual profit rate applied to financier's outstanding ownership VALUE (not a loan balance).
 *   Rate is tiered by FAM score band — same bands as before, but applied to asset co-ownership.
 *   This is the ijarah component: payment for USE of a physical asset, not riba.
 */

export type FAMScoreBand =
  | "excellent"   // 750+
  | "good"        // 700–749
  | "fair"        // 650–699
  | "developing"  // 600–649
  | "entry";      // <600

export interface VehicleAsset {
  id?: string;
  make: string;
  model: string;
  year: number;
  purchasePrice: number;
  depreciationSchedule: "straight-line" | "declining-balance";
  depreciationRateAnnual: number;
  imageUrl?: string;
  dealerName?: string;
  dealerCity?: string;
  dealerState?: string;
  dealerZip?: string;
}

export interface OwnershipSplit {
  creatorContribution: number;
  famContribution: number;       // legacy — set to 0 (FAM uses 5% sponsor reserve at token level)
  investorContribution: number;
  gifterContribution: number;    // legacy — gifts fold into creatorContribution
  allInPrice?: number;           // vehicle + capitalized co-owner costs
}

/** FAM sponsor reserve: 9% of tokens minted at creation, zero capital */
export const FAM_SPONSOR_RESERVE_PCT = 0.09;

/** Co-owner shared costs capitalized into campaign target */
export const CAPITALIZED_COSTS = {
  warrantyMonthly: 75,
  llcFormation: 650,
  llcAgentAnnual: 175,
} as const;

/** Routine maintenance — creator-only pass-through */
export const ROUTINE_MAINTENANCE_MONTHLY = 30;

/** Calculate capitalized co-owner costs for a given term */
export function calcCapitalizedCosts(termMonths: number): number {
  const years = termMonths / 12;
  return (
    CAPITALIZED_COSTS.warrantyMonthly * termMonths +
    CAPITALIZED_COSTS.llcFormation +
    CAPITALIZED_COSTS.llcAgentAnnual * Math.ceil(years)
  );
}

export interface MusharakaTerms {
  vehicleAsset: VehicleAsset;
  ownershipSplit: OwnershipSplit;
  scoreBand: FAMScoreBand;
  termMonths: number;
  monthlyPayment: number;
}

export interface MusharakaAmortizationRow {
  month: number;
  vehicleCurrentValue: number;
  financierOwnershipPct: number;
  financierOwnershipValue: number;
  rentComponent: number;
  unitPurchaseComponent: number;
  creatorOwnershipPct: number;
  cumulativeUnitsPurchased: number;
}

export interface MusharakaResult {
  terms: MusharakaTerms;
  annualProfitRate: number;
  monthlyProfitRate: number;
  initialCreatorOwnershipPct: number;
  totalRentPaid: number;
  totalUnitsPurchased: number;
  totalCost: number;
  effectiveAPR: number;
  schedule: MusharakaAmortizationRow[];
  isShariahCompliant: boolean;
  complianceNotes: string[];
}

export interface ScenarioOption {
  termMonths: number;
  minimumMonthlyPayment: number;
  totalCost: number;
  totalRentPaid: number;
  effectiveAPR: number;
  result: MusharakaResult;
}

export interface GifterReturn {
  gifterContribution: number;
  gifterOwnershipPct: number;
  monthlyRentShare: (month: number) => number;
  totalRentShareOverTerm: number;
  totalReturnOnContribution: number;
}

export const FAM_PROFIT_RATES: Record<FAMScoreBand, number> = {
  excellent:   0.069,
  good:        0.089,
  fair:        0.109,
  developing:  0.139,
  entry:       0.159,
};

export function getFAMScoreBand(famScore: number): FAMScoreBand {
  if (famScore >= 750) return "excellent";
  if (famScore >= 700) return "good";
  if (famScore >= 650) return "fair";
  if (famScore >= 600) return "developing";
  return "entry";
}

export function getVehicleValueAtMonth(vehicle: VehicleAsset, month: number): number {
  const years = month / 12;
  if (vehicle.depreciationSchedule === "straight-line") {
    const depreciatedValue = vehicle.purchasePrice * (1 - vehicle.depreciationRateAnnual * years);
    return Math.max(depreciatedValue, vehicle.purchasePrice * 0.10);
  }
  if (vehicle.depreciationSchedule === "declining-balance") {
    return vehicle.purchasePrice * Math.pow(1 - vehicle.depreciationRateAnnual, years);
  }
  return vehicle.purchasePrice;
}

export function calculateDiminishingMusharaka(terms: MusharakaTerms): MusharakaResult {
  const { vehicleAsset, ownershipSplit, scoreBand, termMonths, monthlyPayment } = terms;
  const annualProfitRate = FAM_PROFIT_RATES[scoreBand];
  const monthlyProfitRate = annualProfitRate / 12;

  const totalFinancierContribution =
    ownershipSplit.famContribution +
    ownershipSplit.investorContribution +
    ownershipSplit.gifterContribution;

  const totalContributions = ownershipSplit.creatorContribution + totalFinancierContribution;

  if (Math.abs(totalContributions - vehicleAsset.purchasePrice) > 1) {
    throw new Error(
      `Ownership contributions (${totalContributions.toFixed(2)}) don't match vehicle price (${vehicleAsset.purchasePrice.toFixed(2)})`
    );
  }

  const initialCreatorOwnershipPct = ownershipSplit.creatorContribution / vehicleAsset.purchasePrice;
  let creatorOwnershipPct = initialCreatorOwnershipPct;

  const schedule: MusharakaAmortizationRow[] = [];
  let totalRentPaid = 0;
  let totalUnitsPurchased = 0;
  let cumulativeUnitsPurchased = 0;

  for (let month = 1; month <= termMonths; month++) {
    const vehicleCurrentValue = getVehicleValueAtMonth(vehicleAsset, month);
    const financierOwnershipPct = 1 - creatorOwnershipPct;
    const financierOwnershipValue = vehicleCurrentValue * financierOwnershipPct;
    const rentComponent = financierOwnershipValue * monthlyProfitRate;
    let unitPurchaseComponent = monthlyPayment - rentComponent;
    if (unitPurchaseComponent < 0) unitPurchaseComponent = 0;
    const ownershipGained = unitPurchaseComponent / vehicleCurrentValue;
    const effectiveOwnershipGained = Math.min(ownershipGained, 1 - creatorOwnershipPct);
    const effectiveUnitPurchase = effectiveOwnershipGained * vehicleCurrentValue;
    cumulativeUnitsPurchased += effectiveUnitPurchase;
    creatorOwnershipPct += effectiveOwnershipGained;
    totalRentPaid += rentComponent;
    totalUnitsPurchased += effectiveUnitPurchase;
    schedule.push({
      month,
      vehicleCurrentValue,
      financierOwnershipPct: 1 - creatorOwnershipPct,
      financierOwnershipValue: vehicleCurrentValue * (1 - creatorOwnershipPct),
      rentComponent,
      unitPurchaseComponent: effectiveUnitPurchase,
      creatorOwnershipPct,
      cumulativeUnitsPurchased,
    });
    if (creatorOwnershipPct >= 0.9999) break;
  }

  const totalCost = totalRentPaid + totalUnitsPurchased;
  const effectiveAPR = calculateEffectiveAPR(totalFinancierContribution, monthlyPayment, termMonths);

  const complianceNotes: string[] = [];
  let isShariahCompliant = true;

  if (ownershipSplit.creatorContribution < vehicleAsset.purchasePrice * 0.05) {
    complianceNotes.push("WARNING: Creator contribution below 5%");
    isShariahCompliant = false;
  }
  if (annualProfitRate > 0.30) {
    complianceNotes.push("WARNING: Profit rate exceeds 30%");
    isShariahCompliant = false;
  }
  const finalOwnership = schedule[schedule.length - 1]?.creatorOwnershipPct ?? 0;
  if (finalOwnership < 0.999) {
    complianceNotes.push(`WARNING: Creator only reaches ${(finalOwnership * 100).toFixed(1)}% ownership by term end`);
  }
  if (complianceNotes.length === 0) {
    complianceNotes.push("✓ Structure passes basic Shariah compliance checks");
    complianceNotes.push("✓ Rent based on asset ownership value, not loan balance");
    complianceNotes.push("✓ Both parties share depreciation risk proportionally");
    complianceNotes.push("Note: Full Shariah compliance requires independent scholar review");
  }

  return {
    terms, annualProfitRate, monthlyProfitRate, initialCreatorOwnershipPct,
    totalRentPaid, totalUnitsPurchased, totalCost, effectiveAPR, schedule,
    isShariahCompliant, complianceNotes,
  };
}

export function calculateMinimumMonthlyPayment(
  vehicleAsset: VehicleAsset,
  ownershipSplit: OwnershipSplit,
  scoreBand: FAMScoreBand,
  termMonths: number,
  tolerance: number = 0.01
): number {
  let low = 1;
  let high = vehicleAsset.purchasePrice;
  let bestPayment = high;
  for (let i = 0; i < 60; i++) {
    const mid = (low + high) / 2;
    const terms: MusharakaTerms = { vehicleAsset, ownershipSplit, scoreBand, termMonths, monthlyPayment: mid };
    try {
      const result = calculateDiminishingMusharaka(terms);
      const finalOwnership = result.schedule[result.schedule.length - 1]?.creatorOwnershipPct ?? 0;
      if (finalOwnership >= 0.999) { bestPayment = mid; high = mid; }
      else { low = mid; }
    } catch { high = mid; }
    if (high - low < tolerance) break;
  }
  return Math.ceil(bestPayment * 100) / 100;
}

export function generateScenarioOptions(
  vehicleAsset: VehicleAsset,
  ownershipSplit: OwnershipSplit,
  scoreBand: FAMScoreBand,
  termOptions: number[] = [24, 36, 48]
): ScenarioOption[] {
  return termOptions.map((termMonths) => {
    const minimumMonthlyPayment = calculateMinimumMonthlyPayment(vehicleAsset, ownershipSplit, scoreBand, termMonths);
    const result = calculateDiminishingMusharaka({ vehicleAsset, ownershipSplit, scoreBand, termMonths, monthlyPayment: minimumMonthlyPayment });
    return { termMonths, minimumMonthlyPayment, totalCost: result.totalCost, totalRentPaid: result.totalRentPaid, effectiveAPR: result.effectiveAPR, result };
  });
}

export function calculateGifterReturn(result: MusharakaResult): GifterReturn {
  const { terms } = result;
  const { ownershipSplit, vehicleAsset } = terms;
  const totalFinancierContribution = ownershipSplit.famContribution + ownershipSplit.investorContribution + ownershipSplit.gifterContribution;
  const gifterOwnershipPct = ownershipSplit.gifterContribution / vehicleAsset.purchasePrice;
  const gifterShareOfFinancierPool = totalFinancierContribution > 0 ? ownershipSplit.gifterContribution / totalFinancierContribution : 0;
  let totalRentShare = 0;
  result.schedule.forEach((row) => { totalRentShare += row.rentComponent * gifterShareOfFinancierPool; });
  return {
    gifterContribution: ownershipSplit.gifterContribution,
    gifterOwnershipPct,
    monthlyRentShare: (month: number) => {
      const row = result.schedule.find((r) => r.month === month);
      return row ? row.rentComponent * gifterShareOfFinancierPool : 0;
    },
    totalRentShareOverTerm: totalRentShare,
    totalReturnOnContribution: totalRentShare + ownershipSplit.gifterContribution,
  };
}

function calculateEffectiveAPR(principal: number, monthlyPayment: number, termMonths: number): number {
  let r = 0.01;
  for (let i = 0; i < 1000; i++) {
    const pv = monthlyPayment * (1 - Math.pow(1 + r, -termMonths)) / r;
    const dpv = monthlyPayment * (
      (termMonths * Math.pow(1 + r, -(termMonths + 1))) / r -
      (1 - Math.pow(1 + r, -termMonths)) / (r * r)
    );
    const delta = (pv - principal) / dpv;
    r -= delta;
    if (Math.abs(delta) < 1e-10) break;
  }
  return r * 12;
}

// Helper for converting dealer vehicle to VehicleAsset
export function toVehicleAsset(vehicle: {
  id?: string; year: number; make: string; model: string; price: number;
  mileage?: number; imageUrl?: string; dealerName?: string;
}): VehicleAsset {
  const currentYear = new Date().getFullYear();
  const age = currentYear - vehicle.year;
  let depreciationSchedule: 'straight-line' | 'declining-balance';
  let depreciationRateAnnual: number;
  if (age <= 5) { depreciationSchedule = 'declining-balance'; depreciationRateAnnual = 0.18; }
  else if (age <= 10) { depreciationSchedule = 'declining-balance'; depreciationRateAnnual = 0.12; }
  else { depreciationSchedule = 'straight-line'; depreciationRateAnnual = 0.08; }
  return {
    id: vehicle.id,
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    purchasePrice: vehicle.price,
    depreciationSchedule,
    depreciationRateAnnual,
    imageUrl: vehicle.imageUrl,
    dealerName: vehicle.dealerName,
  };
}

// Book value estimate (placeholder for VIN API integration)
export function estimateBookValue(vehicle: { year: number; price: number; mileage?: number }): number {
  // TODO: Replace with Marketcheck/NADA API call using VIN + mileage + trim
  const currentYear = new Date().getFullYear();
  const age = currentYear - vehicle.year;
  const ageFactor = Math.max(0.3, 1 - age * 0.12);
  const mileage = vehicle.mileage ?? age * 12000;
  const expectedMileage = age * 12000;
  const mileageRatio = expectedMileage > 0 ? mileage / expectedMileage : 1;
  const mileageDiscount = mileageRatio > 1.2 ? 0.05 : 0;
  return Math.round(vehicle.price * ageFactor * (1 - mileageDiscount));
}

// Dealer offer calculation — FAM pays cash only
export function calculateDealerOffer(vehicle: { price: number; year: number; mileage?: number }): {
  cashOffer: number;
  bookValue: number;
  offerExplanation: string;
} {
  const bookValue = estimateBookValue(vehicle);
  if (vehicle.price <= bookValue) {
    return { cashOffer: vehicle.price, bookValue, offerExplanation: 'Full asking price — at or below book value' };
  }
  const premiumPct = (vehicle.price - bookValue) / bookValue;
  if (premiumPct <= 0.10) {
    return { cashOffer: vehicle.price, bookValue, offerExplanation: 'Full asking price — within 10% of book value' };
  }
  // Asking significantly above book — FAM caps at book + 10%
  const cashOffer = Math.round(bookValue * 1.10);
  return { cashOffer, bookValue, offerExplanation: 'Capped at book value + 10% — vehicle priced above market' };
}
